import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { PageHeader, Section, Loading } from "../components/Common";
import { Stethoscope, HeartPulse, ShieldAlert, X } from "lucide-react";

const AUTHORISED_ROLES = ["principal", "administrator", "health", "boarding", "deputy_head"];
const EVENT_LABEL = {
  clinic_visit: "Clinic visit",
  minor_injury: "Minor injury",
  medication_administered: "Medication",
  sick_bay_stay: "Sick bay",
  guardian_contacted: "Guardian contact",
  referral_hospital: "Hospital referral",
};

function KPI({ label, value, hint, testid }) {
  return (
    <div className="hl-card p-4" data-testid={testid}>
      <div className="overline mb-1">{label}</div>
      <div className="font-display font-bold text-3xl text-[color:var(--hl-navy)]">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

export default function Clinic() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);

  const canView = AUTHORISED_ROLES.includes(user?.role);

  useEffect(() => {
    if (!canView) return;
    api.medicalSummary().then(({ data }) => setSummary(data)).catch(() => {});
    api.medicalEvents().then(({ data }) => setEvents(data || [])).catch(() => {});
  }, [canView]);

  if (!canView) {
    return (
      <div data-testid="clinic-denied">
        <PageHeader kicker="Health" title="Clinic Workspace" subtitle="Restricted health & clinical information." />
        <div className="hl-card p-8 text-center">
          <ShieldAlert className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <div className="font-display text-xl text-[color:var(--hl-navy)] font-bold mb-2">Access restricted</div>
          <div className="text-slate-500 max-w-md mx-auto text-sm">
            The Clinic workspace is limited to authorised Health, Boarding, Deputy Head, Administrator and Principal roles.
            Your role (<span className="mono">{user?.role || "unknown"}</span>) does not permit access to clinical detail.
          </div>
        </div>
      </div>
    );
  }
  if (!summary) return <Loading />;

  // Today's events — filter to last 24h from newest occurred_at
  const now = new Date();
  const today = events.filter((e) => (new Date(e.occurred_at)).toDateString() === now.toDateString());
  const inSickBay = events.filter((e) => e.disposition === "Sick bay" && !e.return_date);
  const referrals = events.filter((e) => e.event_type === "referral_hospital");
  const followUps = events.filter((e) => e.follow_up_required);
  const boardingUnderObs = events.filter((e) => e.boarding_indicator);
  const demoSeeded = events.filter((e) => e.data_origin === "demo_seed").length;

  return (
    <div data-testid="clinic-page">
      <PageHeader
        kicker="Health"
        title="Clinic & Sick Bay Workspace"
        subtitle="Operational school clinic activity. Longitudinal profiles remain in Health360. AY 2026 · Third Term."
      />

      {demoSeeded === events.length && events.length > 0 && (
        <div className="hl-card p-3 mb-4 bg-amber-50 border-amber-200 text-xs text-amber-900 flex items-center gap-2" data-testid="clinic-provenance-banner">
          <ShieldAlert className="w-4 h-4 flex-shrink-0" />
          <span>All {events.length} records currently displayed are <strong>demo-seeded</strong> for the flagship preview. Provenance preserved on each record — never presented as verified clinical history.</span>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <KPI label="Total events (term)" value={summary.total_events} hint={`${summary.students_with_events} students involved`} testid="clinic-kpi-total" />
        <KPI label="Today" value={today.length} hint="Visits in the last 24h" testid="clinic-kpi-today" />
        <KPI label="In sick bay" value={inSickBay.length} hint="Currently under care" testid="clinic-kpi-sickbay" />
        <KPI label="Hospital referrals" value={referrals.length} hint="Requires external follow-up" testid="clinic-kpi-referrals" />
        <KPI label="Boarding under obs." value={boardingUnderObs.length} hint="Sick-bay or referred boarders" testid="clinic-kpi-boarding" />
      </div>

      <Section title="Today's clinic worklist" actions={<Stethoscope className="w-4 h-4 text-slate-400" />}>
        <div className="hl-card p-0 overflow-hidden" data-testid="clinic-today">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left p-3">Time</th>
                <th className="text-left p-3">Student</th>
                <th className="text-left p-3">Category</th>
                <th className="text-left p-3">Presenting</th>
                <th className="text-left p-3">Disposition</th>
                <th className="text-left p-3">Guardian</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {today.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-400">No recorded events today.</td></tr>}
              {events.slice(0, 30).map((e) => (
                <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`clinic-row-${e.id}`}>
                  <td className="p-3 mono text-xs">{(e.occurred_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="p-3 text-xs mono">{e.student_id.slice(0, 8)}…</td>
                  <td className="p-3 text-xs"><span className="hl-chip">{EVENT_LABEL[e.event_type] || e.event_type}</span></td>
                  <td className="p-3 text-xs">{e.presenting_complaint}</td>
                  <td className="p-3 text-xs">{e.disposition}</td>
                  <td className="p-3 text-xs">{e.guardian_notified ? "Notified" : "—"}</td>
                  <td className="p-3 text-right"><button className="hl-btn-secondary text-xs" onClick={() => setSelected(e)} data-testid={`clinic-open-${e.id}`}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title={`In sick bay (${inSickBay.length})`}>
          <div className="hl-card p-4">
            {inSickBay.length === 0 ? <div className="text-sm text-slate-400">No students in sick bay.</div> :
              <ul className="text-sm space-y-2">
                {inSickBay.slice(0, 10).map((e) => (
                  <li key={e.id} className="flex justify-between border-b border-slate-100 pb-1">
                    <span className="mono text-xs">{e.student_id.slice(0, 8)}…</span>
                    <span className="text-xs text-slate-500">{e.presenting_complaint}</span>
                  </li>
                ))}
              </ul>}
          </div>
        </Section>
        <Section title={`Follow-up required (${followUps.length})`}>
          <div className="hl-card p-4">
            {followUps.length === 0 ? <div className="text-sm text-slate-400">No open follow-ups.</div> :
              <ul className="text-sm space-y-2">
                {followUps.slice(0, 10).map((e) => (
                  <li key={e.id} className="flex justify-between border-b border-slate-100 pb-1">
                    <span className="mono text-xs">{e.student_id.slice(0, 8)}…</span>
                    <span className="text-xs text-slate-500">{EVENT_LABEL[e.event_type]}</span>
                  </li>
                ))}
              </ul>}
          </div>
        </Section>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="clinic-drawer">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="overline">Clinical event</div>
                <div className="font-display font-bold text-xl text-[color:var(--hl-navy)]">{EVENT_LABEL[selected.event_type] || selected.event_type}</div>
                <div className="text-xs text-slate-500 mono">{selected.id}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-400"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <div className="hl-card p-3"><div className="overline mb-1">Occurred</div><div className="mono text-xs">{selected.occurred_at}</div></div>
              <div className="hl-card p-3"><div className="overline mb-1">Student (id)</div><div className="mono text-xs">{selected.student_id}</div></div>
              <div className="hl-card p-3 col-span-2"><div className="overline mb-1">Presenting complaint</div><div>{selected.presenting_complaint}</div></div>
              <div className="hl-card p-3 col-span-2"><div className="overline mb-1">Action taken</div><div>{selected.action_taken}</div></div>
              <div className="hl-card p-3"><div className="overline mb-1">Medication</div><div>{selected.medication_administered || "—"}</div></div>
              <div className="hl-card p-3"><div className="overline mb-1">Disposition</div><div>{selected.disposition}</div></div>
              <div className="hl-card p-3"><div className="overline mb-1">Guardian notified</div><div>{selected.guardian_notified ? "Yes" : "No"}</div></div>
              <div className="hl-card p-3"><div className="overline mb-1">Follow-up</div><div>{selected.follow_up_required ? "Required" : "Not required"}</div></div>
              <div className="hl-card p-3 col-span-2"><div className="overline mb-1">Confidentiality</div><div className="capitalize">{selected.confidentiality || "restricted"}</div></div>
            </div>
            {selected.data_origin === "demo_seed" && (
              <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded flex items-center gap-2" data-testid="clinic-provenance-pill">
                <ShieldAlert className="w-3 h-3" /> Provenance: <span className="mono">{selected.data_origin}</span> · <span className="italic">{selected.provenance_note}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
